import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, interval, merge, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, tap, throttleTime } from 'rxjs/operators';
import { AmendmentLock, AmendmentLockSurface } from '@nrwl/rx-models';
import { AmendmentLockConflictError, AmendmentLockService } from './amendment-lock.service';

/**
 * How often the session checks whether a heartbeat should be sent.
 * The backend expires a lock after 15 minutes without activity, so one minute leaves ample margin.
 */
export const AMENDMENT_LOCK_HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Minimum spacing between recorded local-activity samples.
 */
const ACTIVITY_SAMPLE_MS = 1_000;

/**
 * DOM events that count as local user activity for keeping the amendment session alive.
 */
const ACTIVITY_EVENTS: readonly string[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
];

/**
 * Owns one amendment session for the editor that provides it: acquires the document-level lock,
 * keeps it alive with heartbeats while the user is actually active, and reports when the session
 * is lost to another user.
 *
 * Provide it at the component level so its lifetime matches the editor's. There is no explicit
 * release endpoint: stopping the session simply stops the heartbeats, and the backend releases the
 * lock on amendment completion or expires it after 15 minutes of inactivity.
 *
 * Heartbeats are only sent when local activity (mouse, keyboard, scroll, touch) happened since the
 * previous heartbeat, so an abandoned tab lets the lock age out the same way the backend expects.
 */
@Injectable()
export class AmendmentLockSessionService implements OnDestroy {
  private readonly amendmentLockService = inject(AmendmentLockService);
  private readonly ngZone = inject(NgZone);
  private readonly lostSubject = new Subject<AmendmentLockConflictError>();
  private readonly stopped = new Subject<void>();

  private formId: number | null = null;
  private sourceSurface: AmendmentLockSurface = 'rxphoto';
  private sessionToken = 0;
  private lastActivityAt = 0;
  private lastHeartbeatAt = 0;
  private requestInFlight = false;
  private subscriptions = new Subscription();

  /**
   * Emits (inside the Angular zone) when the session can no longer be kept: another user now holds
   * the lock. The session is already stopped when this fires; the editor should alert and leave.
   */
  public readonly lost$: Observable<AmendmentLockConflictError> = this.lostSubject.asObservable();

  /**
   * Whether a session is currently being kept alive.
   */
  public get isActive(): boolean {
    return this.formId !== null;
  }

  /**
   * Acquires the lock for the form and, on success, starts keeping it alive.
   * Any previous session owned by this service is stopped first. The returned observable is bound
   * to the session: a {@link stop} issued while the request is in flight completes it silently,
   * so a superseded acquisition can neither revive heartbeats nor raise the caller's error alert.
   * Errors (including {@link AmendmentLockConflictError} on 409) propagate to the subscriber.
   */
  public start(
    formId: number,
    sourceSurface: AmendmentLockSurface = 'rxphoto',
  ): Observable<AmendmentLock> {
    this.stop();
    const token = ++this.sessionToken;

    return this.amendmentLockService.acquireAmendmentLock(formId, sourceSurface).pipe(
      takeUntil(this.stopped),
      tap(() => {
        // Ignore a late response from a start() call that was superseded by a newer one.
        if (token === this.sessionToken) {
          this.beginSession(formId, sourceSurface);
        }
      }),
    );
  }

  /**
   * Stops heartbeats and activity tracking. Safe to call when no session is active.
   */
  public stop(): void {
    // Cancel an acquisition that is still in flight: invalidate its token and complete its
    // observable, so a late response can neither revive the session nor reach the caller.
    this.sessionToken++;
    this.stopped.next();
    this.endActiveSession();
  }

  public ngOnDestroy(): void {
    this.stop();
    this.stopped.complete();
    this.lostSubject.complete();
  }

  /**
   * Tears down the running session (activity tracking, heartbeat timer, in-flight heartbeat)
   * without touching a pending acquisition. Kept separate from {@link stop} because
   * {@link beginSession} runs inside the acquisition pipeline: signalling `stopped` there would
   * complete that observable before it delivers the acquired lock to the caller.
   */
  private endActiveSession(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.formId = null;
    this.requestInFlight = false;
  }

  private beginSession(formId: number, sourceSurface: AmendmentLockSurface): void {
    this.endActiveSession();
    this.formId = formId;
    this.sourceSurface = sourceSurface;

    const now = Date.now();
    this.lastActivityAt = now;
    this.lastHeartbeatAt = now;

    // Activity sampling and the timer run outside Angular so mouse movement does not trigger
    // change detection; lost$ re-enters the zone explicitly.
    this.ngZone.runOutsideAngular(() => {
      this.subscriptions.add(
        merge(
          ...ACTIVITY_EVENTS.map((eventName) =>
            fromEvent(document, eventName, { passive: true, capture: true }),
          ),
        )
          .pipe(throttleTime(ACTIVITY_SAMPLE_MS))
          .subscribe(() => {
            this.lastActivityAt = Date.now();
          }),
      );

      this.subscriptions.add(
        interval(AMENDMENT_LOCK_HEARTBEAT_INTERVAL_MS).subscribe(() => this.onTick()),
      );
    });
  }

  private onTick(): void {
    if (this.formId === null || this.requestInFlight) {
      return;
    }

    // No local activity since the last heartbeat: let the lock age out on the server.
    if (this.lastActivityAt <= this.lastHeartbeatAt) {
      return;
    }

    this.sendHeartbeat();
  }

  private sendHeartbeat(): void {
    const formId = this.formId;

    if (formId === null) {
      return;
    }

    const previousHeartbeatAt = this.lastHeartbeatAt;
    this.requestInFlight = true;
    this.lastHeartbeatAt = Date.now();

    this.subscriptions.add(
      this.amendmentLockService.heartbeatAmendmentLock(formId).subscribe({
        next: (lock) => {
          this.requestInFlight = false;
          this.handleLockState(lock);
        },
        error: (error: unknown) => {
          this.requestInFlight = false;
          // The attempt didn't count: roll back so the next tick retries even if the user is idle.
          this.lastHeartbeatAt = previousHeartbeatAt;
          this.handleError(error);
        },
      }),
    );
  }

  private handleLockState(lock: AmendmentLock): void {
    if (lock.heldByCurrentUser) {
      return;
    }

    if (lock.locked) {
      this.lose(new AmendmentLockConflictError('amendment_locked', lock.message, lock));
      return;
    }

    // The session expired while nobody else took the form: silently re-acquire it.
    this.reacquire();
  }

  private reacquire(): void {
    const formId = this.formId;

    if (formId === null) {
      return;
    }

    this.requestInFlight = true;

    this.subscriptions.add(
      this.amendmentLockService.acquireAmendmentLock(formId, this.sourceSurface).subscribe({
        next: () => {
          this.requestInFlight = false;
        },
        error: (error: unknown) => {
          this.requestInFlight = false;
          this.handleError(error);
        },
      }),
    );
  }

  private handleError(error: unknown): void {
    if (error instanceof AmendmentLockConflictError) {
      this.lose(error);
    }
    // Any other failure keeps the session and is retried on the next tick. Only a confirmed
    // conflict ends the session: the backend enforces the lock again on `/amend`, so a stale
    // session can never write over someone else's amendment, and kicking the user out of the
    // editor on a transient failure would be worse than a late heartbeat.
  }

  private lose(error: AmendmentLockConflictError): void {
    this.stop();
    this.ngZone.run(() => this.lostSubject.next(error));
  }
}
